import { useEffect, useRef, useState } from "react";
import Icon from "@/components/icon"
import { VitrinMiniAppHeaderMiniapp } from "@/models/miniapp/VitrinMiniAppHeader"
import { useProfileStore } from "@/store/profile-store";
import { openEitaaLink } from "@/utils/utilities";
import Image from "@/components/image";

interface Props {
    miniapp: VitrinMiniAppHeaderMiniapp;
    usersCount: number;
    coursesCount: number;
}

/**
 * Publisher bios run long — a wall of text is the last thing a visitor wants
 * between them and the courses. Three lines, the third fading out, and the rest
 * one tap away.
 */
function MiniappBio({ text }: { text: string }) {
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const ref = useRef<HTMLParagraphElement>(null);

    // Only measured while collapsed: expanding removes the clamp, which would
    // otherwise report "fits" and take the collapse control away with it.
    useEffect(() => {
        if (expanded) return;
        const node = ref.current;
        if (!node) return;

        const measure = () =>
            setOverflows(node.scrollHeight - node.clientHeight > 1);
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [text, expanded]);

    if (!text) return null;

    return (
        <div>
            <div className="relative">
                <p
                    ref={ref}
                    className={`text-sm text-default-600 font-light whitespace-break-spaces leading-6 md:leading-7 ${expanded ? "" : "line-clamp-3"
                        }`}
                >
                    {text}
                </p>

                {!expanded && overflows ? (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-secondary-background to-transparent" />
                ) : null}
            </div>

            {overflows || expanded ? (
                <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="mt-1.5 flex items-center gap-1 text-xs font-medium text-primary"
                >
                    {expanded ? "کمتر" : "بیشتر"}
                    <Icon
                        name="chevron-down"
                        size={16}
                        className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                </button>
            ) : null}
        </div>
    );
}


function MiniappInfo({ miniapp, usersCount, coursesCount }: Props) {

    const { profile } = useProfileStore()


    return (
        // Two columns, and on wide screens the bio moves up beside the cover
        // rather than leaving a column of dead space next to it.
        <section className="grid grid-cols-[auto_1fr] items-start gap-3 p-4 md:gap-x-6 md:p-6">
            <div className="relative ring-2 ring-offset-2 ring-primary rounded-2xl size-fit shrink-0 lg:row-span-2">
                <Image
                    src={miniapp.cover}
                    alt={miniapp.title}
                    width={120}
                    height={120}
                    className="size-[120px] object-cover aspect-square rounded-2xl md:size-40"
                    loading="lazy"
                    decoding="async"
                />
            </div>


            <div className="flex min-w-0 flex-col gap-2 md:gap-3">
                <h1 className="font-semibold text-default-900 md:text-xl">
                    {miniapp.title}
                </h1>

                <div className="flex gap-6 text-xs text-default-500 md:text-sm">
                    <div className="flex items-center gap-1">
                        <Icon
                            name="users"
                            size={18}
                        />
                        <span>
                            {usersCount} دانشجو
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <Icon
                            name="lessons"
                            size={18}
                        />
                        <span>{coursesCount} دوره</span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3">
                    {
                        miniapp.channel_username &&
                        <button onClick={() => openEitaaLink(miniapp.channel_username)} className="rounded-full border border-primary text-primary py-2 w-fit px-2 text-xs font-medium">
                            مشاهده کانال
                        </button>
                    }
                    {
                        miniapp.miniapp_eitaa_username && profile?.is_super_admin &&
                        <button onClick={() => openEitaaLink(miniapp.miniapp_eitaa_username)} className="rounded-full bg-primary/10 text-primary py-2 w-fit px-2 text-xs flex items-center justify-center gap-2">
                            <span>بازکردن برنامک</span>
                            <Icon name="open-miniapp" size={18}></Icon>
                        </button>
                    }
                </div>
            </div>

            <div className="col-span-2 lg:col-span-1 lg:col-start-2">
                <MiniappBio text={miniapp.bio} />
            </div>
        </section>
    )
}

export default MiniappInfo
